/**
 * Presentation wording (ENGINE-SPEC §7.6). While a model is uncalibrated (`calibrated: false`) every user-visible string
 * the ensemble generates uses the uncalibrated wording below and none of the banned words (probability, probable, likely,
 * likelihood, chance, odds, certain(ly), "% AI", verdict, proves, detects). The caveat sentence is mandated verbatim by
 * §7.6 and is the only place "probabilities" appears (T-MOD-04 exempts exactly that literal).
 */

export const CAVEAT_UNCALIBRATED =
	'These values have not been calibrated against a reference corpus. They measure machine-associated patterns; they are not probabilities and not proof of authorship.';

export const CAVEAT_CALIBRATED =
	'The machine-authorship index is calibrated against the reference corpus named in the model card, at the stated reference prior. The model card lists the error rates per stratum; a single index value is not proof of authorship.';

/**
 * The scope sentence that follows `CAVEAT_CALIBRATED` for a learned calibrated model (UD-14, W8b): only the
 * machine-authorship index is calibrated, and it is shown as an index. Free of banned words, so no exemption is needed.
 */
export const CAVEAT_CALIBRATED_SCOPE =
	'Only the machine-authorship index is calibrated: the translation index, the seed-rules composite, the segment strip and the per-channel values are not calibrated and measure machine-associated patterns.';

export interface HeadlineInput {
	calibrated: boolean;
	index: number;
	ci90: [number, number];
	/** 0.9 = 90%. */
	level?: number;
	evidenceCoverage: number;
}

const f1 = (x: number): string => x.toFixed(1);
const f2 = (x: number): string => x.toFixed(2);
const tag = (calibrated: boolean, ref?: number): string =>
	calibrated
		? `(calibrated${ref === undefined ? '' : `, reference prior ${f2(ref)}`})`
		: '(uncalibrated)';

/** "Machine-authorship index (uncalibrated): 58.3 · 90% interval 49.1–66.0 · evidence coverage 0.82" */
export function headline(h: HeadlineInput, referencePrior?: number): string {
	const pct = Math.round((h.level ?? 0.9) * 100);
	return `Machine-authorship index ${tag(h.calibrated, referencePrior)}: ${f1(h.index)} · ${pct}% interval ${f1(h.ci90[0])}–${f1(h.ci90[1])} · evidence coverage ${f2(h.evidenceCoverage)}`;
}

export interface TranslationHeadlineInput {
	calibrated: boolean;
	score: number | null;
	ci90: [number, number] | null;
	kappa: number | null;
	level?: number;
	/** Why MT-01 abstained, when it did. */
	reason?: string;
	/**
	 * A calibrated translation model is loaded but its gate G4 failed (T2-DESIGN §8): the headline keeps the uncalibrated
	 * wording and adds `TRANSLATION_NOT_ESTABLISHED`. Absent when no translation model is loaded (the wording is unchanged).
	 */
	notEstablished?: boolean;
}

/**
 * The document counts behind the translation model's claims (`data/oof/t2-claims.json`, model `t2-gam-4ec263a0`): Claude Sonnet 5.5
 * translations scored out of fold, human translations and human originals used as negatives. A unit test holds them equal to the
 * claims file. Every number in the translation wording carries them (DESIGN CD-2 to CD-4).
 */
export const TRANSLATION_N = {
	machineTranslations: 430,
	humanTranslations: 593,
	humanOriginals: 2633,
} as const;

/** The scope sentence of the translation axis (CD-37, CD-48): the one translation system that was measured. */
export const TRANSLATION_SCOPE =
	'Machine translation here means Claude Sonnet 5.5 translating into English; other translation systems, post-edited translation and source languages outside ko, zh, ja, ru, fr, de, es and it are not established.';

const nf = (n: number): string => n.toLocaleString('en-US');

/**
 * What the headline says while gate G4 has failed: the axis is not established. Free of banned words (no exemption needed).
 * "not established (430 Claude translations, 593 human translations and 2,633 human originals; model card t2-gam-4ec263a0 and the limitations)"
 */
export const TRANSLATION_NOT_ESTABLISHED = `machine translation is not established (${nf(TRANSLATION_N.machineTranslations)} Claude translations, ${nf(TRANSLATION_N.humanTranslations)} human translations and ${nf(TRANSLATION_N.humanOriginals)} human originals; see the model card t2-gam-4ec263a0 and the limitations)`;

/** "Machine-translation index (uncalibrated): 41.2 · 90% interval 30.5–52.0 · data weight 0.63" */
export function translationHeadline(t: TranslationHeadlineInput, referencePrior?: number): string {
	const head = `Machine-translation index ${tag(t.calibrated, referencePrior)}`;
	const tail = t.notEstablished ? ` · ${TRANSLATION_NOT_ESTABLISHED}` : '';
	if (t.score === null || t.ci90 === null)
		return `${head}: not available${t.reason ? ` (MT-01: ${t.reason})` : ''}${tail}`;
	const pct = Math.round((t.level ?? 0.9) * 100);
	return `${head}: ${f1(t.score)} · ${pct}% interval ${f1(t.ci90[0])}–${f1(t.ci90[1])} · data weight ${f2(t.kappa ?? 0)}${tail}`;
}

/** The caveat sentence(s). `scoped` (a learned calibrated model, where only the authorship index is calibrated) adds the scope sentence. */
export const caveat = (calibrated: boolean, scoped = false): string =>
	calibrated
		? scoped
			? `${CAVEAT_CALIBRATED} ${CAVEAT_CALIBRATED_SCOPE}`
			: CAVEAT_CALIBRATED
		: CAVEAT_UNCALIBRATED;

/* ---------------- the calibrated machine-authorship index (UD-14, ED-71, ED-73) ---------------- */

/** Where the calibrated index sits against the reference operating point, plus 'not-available' (no group / no value). */
export type CalibratedStatus =
	| 'flagged'
	| 'at-reference'
	| 'abstain'
	| 'abstain-interval'
	| 'below'
	| 'not-available';

/** The wording of each status, from the engine only (the UI never compares values to the reference point). */
export const STATUS_PHRASE: Record<CalibratedStatus, string> = {
	flagged: 'flagged at the reference point',
	'at-reference': 'at the reference point',
	abstain: 'in the abstention band at the reference point',
	'abstain-interval': 'abstains: the text-sampling interval spans the reference point',
	below: 'below the reference point',
	'not-available': 'not available',
};

/**
 * The status wording of a document whose length group has its own operating point (DESIGN CD-98): a flag raised there is
 * "flagged at the short-text point" (the point's `name`), a document below both points is "below the short-text point",
 * and an interval whose upper end only that point flags "spans the short-text point" (`spans`, DESIGN CD-108 condition 7);
 * every other case is `STATUS_PHRASE`. A model without such a point gets exactly `STATUS_PHRASE[status]`.
 */
export function statusPhraseOf(
	status: CalibratedStatus,
	raisedBy: 'reference' | 'length-group' | null,
	point: { name: string } | null,
	spans: 'reference' | 'length-group' | null = null
): string {
	if (point && status === 'flagged' && raisedBy === 'length-group')
		return `flagged at the ${point.name}`;
	if (point && status === 'below') return `below the ${point.name}`;
	if (point && status === 'abstain-interval' && spans === 'length-group')
		return `abstains: the text-sampling interval spans the ${point.name}`;
	return STATUS_PHRASE[status];
}

/* ---------------- the text-sampling interval of the calibrated index (ED-74) ---------------- */

/** The label of the interval: it varies the text sampled (chapters, scenes or paragraphs), nothing else. */
export const INTERVAL_LABEL = 'text-sampling variation';

/**
 * What the interval does and does not cover (model card and user guide). Free of banned words, so no exemption is needed.
 */
export const INTERVAL_MEANING =
	"The text-sampling interval shows how much the index moves when the document's chapters, scenes or paragraphs are resampled; it does not include the uncertainty of the calibration itself. On the reference corpus it understates the variation between independent samples of the same text (model card), so it never decides the status.";

/** "(text-sampling variation 61.0 to 80.3)" on the index scale. */
export const intervalParen = (lo: number, hi: number): string =>
	`(${INTERVAL_LABEL} ${f1(lo)} to ${f1(hi)})`;

/** "Index 72.4 (text-sampling variation 61.0 to 80.3)": the engine's display text; the UI prints it and never builds it. */
export const intervalPhrase = (index: number, lo: number, hi: number): string =>
	`Index ${f1(index)} ${intervalParen(lo, hi)}`;

/** "B3-B4" shown as "B3–B4". */
export const groupLabel = (g: string): string => g.replace('-', '–');

export interface CalibratedHeadlineInput {
	status: CalibratedStatus;
	/** The calibrated index (100·value), or null when there is none. */
	index: number | null;
	/** The reference point in index units (100·t*). */
	referenceIndex: number;
	referenceName: string;
	group: string | null;
	evidenceCoverage: number;
	/** The shortest length the calibration covers, in prose words (the floor under which the index is not available). */
	floorWords: number;
	/** The length group's own point (CD-98) and which point raised a flag; absent or null for a model without one. */
	lengthGroupPoint?: { name: string; threshold: number } | null;
	raisedBy?: 'reference' | 'length-group' | null;
	/** For 'abstain-interval': the point that flags the interval's upper end (`CalibratedInterval.spans`). */
	spans?: 'reference' | 'length-group' | null;
}

/**
 * "Machine-authorship index (calibrated, reference prior 0.50): 93.1 · flagged at the reference point (strictest-stratum 1%
 * false-positive point, 93.1) · length group B3–B4 · evidence coverage 0.82", or below the floor
 * "Machine-authorship index (calibrated): not available below 150 prose words". Always an index, never a percentage or a
 * probability (UD-14).
 */
export function calibratedHeadline(h: CalibratedHeadlineInput, referencePrior?: number): string {
	if (h.index === null || h.status === 'not-available')
		return `Machine-authorship index (calibrated): not available below ${h.floorWords} prose words`;
	const gp = h.lengthGroupPoint ?? null;
	const atPoint =
		gp &&
		(h.status === 'below' ||
			(h.status === 'flagged' && h.raisedBy === 'length-group') ||
			(h.status === 'abstain-interval' && h.spans === 'length-group'));
	// the cut is on the model logit, never an index (CD-104); "logit", not "log-odds", which the banned-word rule matches
	const cut = gp ? gp.threshold.toFixed(3) : '';
	const paren =
		h.status === 'flagged'
			? `model logit at or above ${cut}`
			: h.status === 'below'
				? `model logit below ${cut}`
				: `${gp?.name} at model logit ${cut}`;
	const where = atPoint
		? `${statusPhraseOf(h.status, h.raisedBy ?? null, gp, h.spans ?? null)} (${paren})`
		: `${STATUS_PHRASE[h.status]} (${h.referenceName}, ${f1(h.referenceIndex)})`;
	const grp = h.group ? ` · length group ${groupLabel(h.group)}` : '';
	return `Machine-authorship index ${tag(true, referencePrior)}: ${f1(h.index)} · ${where}${grp} · evidence coverage ${f2(h.evidenceCoverage)}`;
}

/**
 * The calibrated machine-translation index, shown only when a translation model is loaded and its gate G4 passed. The status
 * words are the authorship index's (flagged, at the reference point, abstain, below) with "translation" in the label; always an
 * index, never a percentage or a probability.
 * "Machine-translation index (calibrated, reference prior 0.50): 93.1 · flagged at the reference point (strictest-stratum 1%
 * false-positive point (translation), 89.6) · length group B3–B4 · Claude Sonnet 5.5 translations only"
 */
export function calibratedTranslationHeadline(
	h: CalibratedHeadlineInput,
	referencePrior?: number
): string {
	if (h.index === null || h.status === 'not-available')
		return `Machine-translation index (calibrated): not available below ${h.floorWords} prose words`;
	const where = `${STATUS_PHRASE[h.status]} (${h.referenceName}, ${f1(h.referenceIndex)})`;
	const grp = h.group ? ` · length group ${groupLabel(h.group)}` : '';
	return `Machine-translation index ${tag(true, referencePrior)}: ${f1(h.index)} · ${where}${grp} · Claude Sonnet 5.5 translations only`;
}

/** "Seed-rules composite (uncalibrated): 58.3 · 90% interval 49.1–66.0" (the v1 composite beside the calibrated index, ED-71). */
export function compositeHeadline(c: {
	index: number;
	ci90: [number, number];
	level?: number;
}): string {
	const pct = Math.round((c.level ?? 0.9) * 100);
	return `Seed-rules composite (uncalibrated): ${f1(c.index)} · ${pct}% interval ${f1(c.ci90[0])}–${f1(c.ci90[1])}`;
}

/** Per-channel note for a channel whose reference-corpus sign at the document's length group is not the stated one. */
export const REFERENCE_SIGN_NOTE: Record<'inverted' | 'none', string> = {
	inverted: 'In the reference corpus this channel reads the other way at this length.',
	none: 'In the reference corpus this channel showed no direction at this length.',
};

/** The banned-word test of §7.6 (word boundaries; the mandated caveat sentence is exempt). */
export const BANNED_WORDS =
	/\b(probability|probabilities|probable|probably|likely|likelihood|chance|odds|certain|certainly|verdicts?|proves?|detects?|detected|detecting)\b|% AI/i;

export function bannedWordsIn(text: string): string | null {
	const t = text.split(CAVEAT_UNCALIBRATED).join('');
	const m = BANNED_WORDS.exec(t);
	return m ? m[0] : null;
}
